import { useSyncExternalStore } from 'react';

/**
 * The currently selected workspace slug. Resolved from the subdomain (`acme.buildbee.app`) when
 * VITE_APP_ROOT_DOMAIN is set, otherwise from localStorage (switcher). Sent to the API on every
 * request as `x-workspace-slug`.
 */

const STORAGE_KEY = 'bb-workspace';
const listeners = new Set<() => void>();

function slugFromSubdomain(): string | null {
  const root = import.meta.env.VITE_APP_ROOT_DOMAIN;
  if (!root || typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  if (!host.endsWith(`.${root}`)) return null;
  const sub = host.slice(0, -(root.length + 1));
  return sub && !sub.includes('.') && sub !== 'app' && sub !== 'www' ? sub : null;
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * `?workspace=<slug>` on the landing URL (Hive single sign-on lands with it): select that workspace
 * once, remember it like the switcher does, and drop the parameter from the address bar.
 * AppLayout still falls back to another workspace when the user is not a member of this one.
 */
function slugFromLandingUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const slug = url.searchParams.get('workspace')?.trim().toLowerCase();
    if (!slug || !/^[a-z0-9][a-z0-9-]{0,62}$/.test(slug)) return null;
    url.searchParams.delete('workspace');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    try {
      localStorage.setItem(STORAGE_KEY, slug);
    } catch {
      // ignore storage failures
    }
    return slug;
  } catch {
    return null;
  }
}

let current: string | null = slugFromSubdomain() ?? slugFromLandingUrl() ?? readStored();

export function getWorkspaceSlug(): string | null {
  return current;
}

export function setWorkspaceSlug(slug: string | null): void {
  if (slug === current) return;
  current = slug;
  try {
    if (slug) localStorage.setItem(STORAGE_KEY, slug);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
  listeners.forEach((l) => l());
}

export function useWorkspaceSlug(): string | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getWorkspaceSlug,
    getWorkspaceSlug,
  );
}

export const apiBase = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
